import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';

// Function to generate signed URL for private storage
const getSignedUrl = async (path) => {
  try {
    console.log('Attempting to generate signed URL for path:', path);
    const { data, error } = await supabase.storage.from('id_documents').createSignedUrl(path, {
      expiresIn: 60 * 60 * 24 * 7, // 7 days
    });
    if (error) {
      console.error('Signed URL generation failed:', error);
      throw error;
    }
    console.log('Signed URL generated successfully:', data.signedUrl);
    return data.signedUrl;
  } catch (error) {
    console.error('Error generating signed URL:', error);
    // Fallback to public URL
    try {
      console.log('Falling back to public URL for path:', path);
      const { data: publicUrlData } = supabase.storage.from('id_documents').getPublicUrl(path);
      console.log('Public URL generated:', publicUrlData?.publicUrl);
      return publicUrlData?.publicUrl || null;
    } catch (publicError) {
      console.error('Error generating public URL:', publicError);
      // Final fallback to direct URL construction
      console.log('Falling back to direct public URL construction');
      return getDirectPublicUrl(path);
    }
  }
};

// Function to directly construct public URL
const getDirectPublicUrl = (path) => {
  const supabaseUrl = 'https://udufxyzdpjczqlozkero.supabase.co/storage/v1/object/public/id_documents/' + path;
  console.log('Direct public URL constructed:', supabaseUrl);
  return supabaseUrl;
};

const FILTERS = [
  { key: 'pending', label: 'Pending' },
  { key: 'verified', label: 'Verified' },
  { key: 'rejected', label: 'Rejected' },
];

const STATUS_LABELS = {
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
};

const ID_TYPE_COLORS = {
  aadhaar: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  pan: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  passport: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  driving_license: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

const getStatusValue = (doc) => {
  const value = doc?.verification_status || doc?.status || 'pending';
  return String(value).toLowerCase();
};

const formatIDType = (value) => {
  if (!value) return 'Unknown';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const maskIDNumber = (idNumber) => {
  if (!idNumber) return 'N/A';
  const cleaned = String(idNumber).replace(/\s+/g, '').trim();
  if (cleaned.length <= 4) return `****${cleaned.slice(-4)}`;
  return `****${cleaned.slice(-4)}`;
};

const getConfidence = (doc) => {
  if (doc?.verification_data?.confidence != null) {
    return `${Number(doc.verification_data.confidence).toFixed(0)}%`;
  }
  if (doc?.confidence != null) {
    return `${Number(doc.confidence).toFixed(0)}%`;
  }
  return 'N/A';
};

export default function AdminIDVerificationTab({ showToast = () => {}, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [activeFilter, setActiveFilter] = useState('pending');
  const [rejectionReasonMap, setRejectionReasonMap] = useState({});
  const [processingId, setProcessingId] = useState(null);
  const [error, setError] = useState('');
  const [imageErrors, setImageErrors] = useState({});
  const [signedUrls, setSignedUrls] = useState({});

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError('');

      const { data, error: queryError } = await supabase
        .from('id_documents')
        .select(
          '*, users!id_documents_user_id_fkey(name, email)'
        )
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      const documents = data || [];

      // Generate signed URLs for each document
      const urlPromises = documents.map(async (doc) => {
        if (doc.file_url) {
          console.log('Processing doc.file_url for doc', doc.id, ':', doc.file_url);
          const signedUrl = await getSignedUrl(doc.file_url);
          console.log('Generated signed URL for doc', doc.id, ':', signedUrl);
          return { id: doc.id, signedUrl };
        }
        console.log('No file_url for doc', doc.id);
        return { id: doc.id, signedUrl: null };
      });

      const urlResults = await Promise.all(urlPromises);
      const urlMap = {};
      urlResults.forEach(({ id, signedUrl }) => {
        urlMap[id] = signedUrl;
      });

      setSignedUrls(urlMap);
      setDocuments(documents);
    } catch (err) {
      console.error('Failed to fetch ID verification documents:', err);
      setError(err?.message || 'Unable to load ID verification records.');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => getStatusValue(doc) === activeFilter);
  }, [documents, activeFilter]);

  const updateDocumentStatus = async (doc, newStatus, reason = '') => {
    if (!doc?.id) return;

    setProcessingId(doc.id);
    setError('');

    try {
      const updatePayload = {
        verification_status: newStatus,
        status: newStatus,
      };

      if (newStatus === 'rejected') {
        updatePayload.admin_notes = reason || 'Rejected by admin';
      }

      const { error: updateError } = await supabase
        .from('id_documents')
        .update(updatePayload)
        .eq('id', doc.id);

      if (updateError) throw updateError;

      const { error: userError } = await supabase
        .from('users')
        .update({
          id_verification_status: newStatus,
          ...(newStatus === 'rejected'
            ? { id_rejected_reason: reason || 'Rejected by admin' }
            : {}),
        })
        .eq('id', doc.user_id);

      if (userError) throw userError;

      const toastMessage =
        newStatus === 'verified'
          ? 'ID document approved successfully.'
          : 'ID document rejected successfully.';

      showToast(toastMessage, 'success');
      await fetchDocuments();
    } catch (err) {
      console.error('Failed to update document verification status:', err);
      setError(err?.message || 'Unable to update verification status.');
      showToast('Failed to update verification status.', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleApprove = async (doc) => {
    await updateDocumentStatus(doc, 'verified');
  };

  const handleReject = async (doc) => {
    const reason = (rejectionReasonMap[doc.id] || '').trim();

    if (!reason) {
      setError('Please provide a rejection reason before rejecting this document.');
      return;
    }

    await updateDocumentStatus(doc, 'rejected', reason);
    setRejectionReasonMap((prev) => ({ ...prev, [doc.id]: '' }));
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 rounded-2xl border border-dark-500 bg-dark-800 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-400">Admin controls</p>
          <h3 className="mt-2 text-2xl font-semibold text-white">ID Verification Review</h3>
        </div>

        <button
          type="button"
          onClick={fetchDocuments}
          className="inline-flex items-center gap-2 rounded-xl border border-dark-400 bg-dark-700 px-3 py-2 text-sm text-slate-200 transition hover:border-dark-300"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {FILTERS.map((filter) => (
          <button
            key={filter.key}
            type="button"
            onClick={() => setActiveFilter(filter.key)}
            className={`rounded-xl border px-4 py-2 text-sm font-medium transition ${
              activeFilter === filter.key
                ? 'border-amber-500 bg-amber-500/15 text-amber-300'
                : 'border-dark-400 bg-dark-700 text-slate-300 hover:border-dark-300 hover:text-white'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[260px] items-center justify-center rounded-2xl border border-dark-500 bg-dark-800">
          <div className="flex items-center gap-3 text-slate-300">
            <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
            Loading verification records...
          </div>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="rounded-2xl border border-dark-500 bg-dark-800 p-8 text-center text-slate-400">
          <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-slate-500" />
          No {activeFilter} ID verification documents found.
        </div>
      ) : (
        <div className="grid gap-5 xl:grid-cols-2">
          {filteredDocuments.map((doc) => {
            const user = doc.users || {};
            const status = getStatusValue(doc);
            const docType = formatIDType(doc.id_type || doc.document_type || doc.verification_data?.idType || 'aadhaar');
            const docTypeKey = (doc.id_type || doc.document_type || doc.verification_data?.idType || 'aadhaar').toLowerCase();

            return (
              <div
                key={doc.id}
                className="rounded-2xl border border-dark-500 bg-dark-800 p-4 shadow-lg shadow-black/10"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-semibold text-white">{user.name || 'Unknown User'}</p>
                    <p className="text-sm text-slate-400">{user.email || 'No email available'}</p>
                  </div>

                  <span
                    className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${
                      ID_TYPE_COLORS[docTypeKey] || 'bg-dark-700 text-slate-300 border-dark-400'
                    }`}
                  >
                    {docType}
                  </span>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-[1.1fr_0.9fr]">
                  <div className="space-y-3">
                    <div className="rounded-xl border border-dark-500 bg-dark-700 p-3">
                      <p className="mb-1 text-xs uppercase tracking-[0.15em] text-slate-500">Holder</p>
                      <p className="text-sm font-medium text-white">{doc.holder_name || doc.verification_data?.name || 'Not available'}</p>
                    </div>

                    <div className="rounded-xl border border-dark-500 bg-dark-700 p-3">
                      <p className="mb-1 text-xs uppercase tracking-[0.15em] text-slate-500">ID Number</p>
                      <p className="text-sm font-medium text-white">
                        {maskIDNumber(doc.id_number || doc.document_number || doc.verification_data?.idNumber)}
                      </p>
                    </div>

                  </div>

                  <div className="space-y-3">
                    <div className="overflow-hidden rounded-xl border border-dark-500 bg-dark-700">
                      {doc.file_url ? (
                        <>
                          <img
                            src={signedUrls[doc.id] || doc.file_url}
                            alt="ID document preview"
                            className="h-52 w-full object-contain bg-dark-800"
                            style={{ display: imageErrors[doc.id] ? 'none' : 'block' }}
                            onError={(e) => {
                              console.error('Image load error for doc', doc.id, ':', e);
                              console.error('Failed URL:', signedUrls[doc.id] || doc.file_url);
                              setImageErrors(prev => ({ ...prev, [doc.id]: true }));
                            }}
                            onLoad={() => {
                              console.log('Image loaded successfully for doc', doc.id, ':', signedUrls[doc.id] || doc.file_url);
                              setImageErrors(prev => ({ ...prev, [doc.id]: false }));
                            }}
                          />
                          {imageErrors[doc.id] && (
                            <div className="flex h-52 items-center justify-center text-slate-400">
                              <div className="text-center">
                                <AlertCircle className="mx-auto mb-2 h-8 w-8 text-red-400" />
                                <p className="text-sm">Unable to load image</p>
                                <p className="text-xs text-slate-500 mt-1">URL: {(signedUrls[doc.id] || doc.file_url)?.substring(0, 50)}...</p>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex h-52 items-center justify-center text-slate-400">
                          <div className="text-center">
                            <ImageIcon className="mx-auto mb-2 h-8 w-8" />
                            No preview
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl border border-dark-500 bg-dark-700 p-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">Confidence</span>
                        <span className="font-medium text-white">{getConfidence(doc)}</span>
                      </div>
                    </div>

                    <div className="rounded-xl border border-dark-500 bg-dark-700 p-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">Status</span>
                        <span className="font-medium text-white">{STATUS_LABELS[status] || 'Pending'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {status === 'pending' && (
                  <div className="mt-5 space-y-3 border-t border-dark-500 pt-4">
                    <div className="space-y-2">
                      <label className="text-xs uppercase tracking-[0.15em] text-slate-500">Rejection reason</label>
                      <textarea
                        value={rejectionReasonMap[doc.id] || ''}
                        onChange={(e) =>
                          setRejectionReasonMap((prev) => ({
                            ...prev,
                            [doc.id]: e.target.value,
                          }))
                        }
                        placeholder="Optional rejection reason"
                        className="min-h-[80px] w-full rounded-xl border border-dark-400 bg-dark-700 px-3 py-2 text-sm text-white outline-none placeholder:text-slate-500 focus:border-amber-500"
                      />
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => handleApprove(doc)}
                        disabled={processingId === doc.id}
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-dark-500"
                      >
                        {processingId === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ShieldCheck className="h-4 w-4" />
                        )}
                        Approve
                      </button>

                      <button
                        type="button"
                        onClick={() => handleReject(doc)}
                        disabled={processingId === doc.id}
                        className="inline-flex items-center gap-2 rounded-xl bg-red-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-400 disabled:cursor-not-allowed disabled:bg-dark-500"
                      >
                        {processingId === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <XCircle className="h-4 w-4" />
                        )}
                        Reject
                      </button>
                    </div>
                  </div>
                )}

                {status !== 'pending' && (
                  <div className="mt-5 rounded-xl border border-dark-500 bg-dark-700 p-3 text-sm text-slate-300">
                    {status === 'verified' ? (
                      <div className="flex items-center gap-2 text-emerald-300">
                        <CheckCircle2 className="h-4 w-4" />
                        Approved by admin
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-300">
                        <XCircle className="h-4 w-4" />
                        {doc.admin_notes || 'Rejected by admin'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
